import { gql, useQuery } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import { Flags } from '@constants/flags';
import { useFlag } from '@onevesthq/feature-flags/web';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { DroppableFileInput } from '@ovComponents/2-component/droppableFileInput/droppableFileInput';
import { PdfViewer } from '@ovComponents/2-component/pdfViewer/pdfViewer';
import { safeWindowOpen } from '@utils/urlSanitization';
import { Box, Button, Skeleton, Typography, useToast } from 'hephaestus/web';
import { Create, Download, Upload } from 'hephaestus/webIcons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../hooks/useLocalization';

const FETCH_FORM_COMPLETION = gql`
  query fetchFormCompletion($formCompletionId: ObjectID!) {
    fetchFormCompletion(formCompletionId: $formCompletionId) {
      id
      state
      templateType
      signingType
      unsignedUrl
      signedUrls
      signedDocuments {
        id
        downloadUrl
      }
      unsignedDocument {
        id
        downloadUrl
      }
      translatedName { ${TRANSLATED_STRING_FIELDS} }
      resolvedPdfFileName { ${TRANSLATED_STRING_FIELDS} }
      translatedDescription { ${TRANSLATED_STRING_FIELDS} }
    }
  }
`;

export const ViewFormDialog = ({
  formCompletionId,
  onClose,
  sign,
  file,
  setFile,
  signLoading,
}: {
  formCompletionId: any;
  onClose: () => void;
  sign?: () => void;
  file?: File;
  setFile: (file: File) => void;
  signLoading: boolean;
}) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation(['workflowCompletions', 'shared']);
  const { enqueueToast } = useToast();
  const [formCompletion, setFormCompletion] = useState<any>(null);
  const { enabled: overwriteReuploadEnabled } = useFlag(
    Flags.form_completion_reupload_overwrite,
    { default: false },
  );

  const { loading } = useQuery(FETCH_FORM_COMPLETION, {
    variables: { formCompletionId },
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      setFormCompletion(data.fetchFormCompletion);
    },
  });

  // TODO: Remove signedUrls once we have transitioned fully to signedDocuments
  const signedPdfUrl =
    formCompletion?.signedDocuments?.[0]?.downloadUrl ||
    formCompletion?.signedUrls?.[0];
  const unsignedPdfUrl = overwriteReuploadEnabled
    ? formCompletion?.unsignedDocument?.downloadUrl ||
      formCompletion?.unsignedUrl
    : formCompletion?.unsignedUrl;
  const pdfUrl = overwriteReuploadEnabled
    ? signedPdfUrl || unsignedPdfUrl
    : signedPdfUrl ||
      formCompletion?.unsignedUrl ||
      formCompletion?.unsignedDocument?.downloadUrl;

  return (
    <Dialog open={true} onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle onClose={onClose}>
        {loading ? (
          <Skeleton width={200} />
        ) : (
          translateBackendString(formCompletion.resolvedPdfFileName) ||
          translateBackendString(formCompletion.translatedName)
        )}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Skeleton height={500} width='100%' />
        ) : (
          <Box>
            <Typography variant='bodyLarge'>
              {translateBackendString(formCompletion.translatedDescription)}
            </Typography>
            {pdfUrl && <PdfViewer pdfUrl={pdfUrl} fullWidth />}
            {formCompletion.signingType === 'UPLOAD' &&
              formCompletion.state === 'UNSIGNED' && (
                <>
                  {formCompletion.templateType === 'PDF' && (
                    <Typography variant='bodyLarge'>
                      {t('workflowCompletions:uploadSignedForm')}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      mt: 2,
                    }}
                  >
                    <Box sx={{ marginBottom: '16px' }}>
                      <DroppableFileInput
                        onFileChosen={(dropFile: File) => {
                          setFile(dropFile);
                        }}
                      />
                    </Box>
                  </Box>
                </>
              )}
          </Box>
        )}
      </DialogContent>
      {!loading && (
        <DialogFooter>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Box>
              {formCompletion.templateType === 'PDF' &&
                unsignedPdfUrl &&
                formCompletion.state === 'UNSIGNED' && (
                  <Button
                    onClick={() =>
                      safeWindowOpen(unsignedPdfUrl, {
                        target: '_blank',
                        onError: (errorMsg) => {
                          enqueueToast(errorMsg, { variant: 'error' });
                        },
                      })
                    }
                    variant='contained'
                    startIcon={<Download />}
                    color='secondary'
                  >
                    {t('shared:download')}
                  </Button>
                )}
            </Box>
            {formCompletion.state === 'UNSIGNED' &&
              sign &&
              (formCompletion.signingType === 'UPLOAD' ? (
                <Button
                  onClick={sign}
                  startIcon={<Upload />}
                  disabled={!file || signLoading}
                >
                  {t('shared:upload')}
                </Button>
              ) : (
                <Button
                  onClick={sign}
                  startIcon={<Create />}
                  disabled={signLoading}
                >
                  {t('shared:sign')}
                </Button>
              ))}
            {formCompletion.state === 'SIGNED' && signedPdfUrl && (
              <Button
                onClick={() =>
                  safeWindowOpen(signedPdfUrl, {
                    target: '_blank',
                    onError: (errorMsg) => {
                      enqueueToast(errorMsg, { variant: 'error' });
                    },
                  })
                }
                startIcon={<Download />}
              >
                {t('shared:download')}
              </Button>
            )}
          </Box>
        </DialogFooter>
      )}
    </Dialog>
  );
};
